import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_API_KEY = '/donation/chart';

const transformChartData = (response: Api.IDonationReportingChartsResponse): Api.IDonationReportingCharts => {
	const charts = response.charts || {};

	return {
		amountDistribution:
			charts.amountDistribution?.data?.map((item: Api.IDonationChartDataPoint) => ({
				range: item.label,
				count: item.count,
				amount: item.value,
			})) || [],
		byCampaign:
			charts.byCampaign?.data?.map((item: Api.IDonationChartDataPoint) => ({
				campaign: item.label,
				amount: item.value,
				count: item.count,
			})) || [],
		byTag:
			charts.byTag?.data?.map((item: Api.IDonationChartDataPoint) => ({
				tag: item.label,
				amount: item.value,
				count: item.count,
			})) || [],
		overTime:
			charts.overTime?.data?.map((item: Api.IDonationChartDataPoint) => ({
				date: item.label,
				amount: item.value,
				count: item.count,
			})) || [],
	};
};

export const useDonationReportingChartsQuery = ({
	filterRequest,
	enabled,
	refetchOnWindowFocus = false,
	charts,
}: {
	filterRequest: Api.IDonationFilterRequest;
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	charts: Api.IDonationChartConfiguration[];
}) => {
	const userSession = useUserSession();
	return useQuery<Api.IDonationReportingCharts>({
		enabled,
		queryFn: async (): Promise<Api.IDonationReportingCharts> => {
			const response = await userSession.webServiceHelper.callAsync<Api.IDonationReportingChartsResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `donation/charts`,
				}),
				'POST',
				{ filter: filterRequest, charts }
			);

			return transformChartData(response);
		},
		queryKey: [BASE_API_KEY, filterRequest, charts],
		refetchOnWindowFocus,
	});
};

export const invalidateAllDonationReportingCharts = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY] });
};

export const invalidateDonationReportingCharts = ({ filterRequest }: { filterRequest: Api.IDonationFilterRequest }) => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY, filterRequest] });
};
